/**
 * NiV tool — reference cards: plan limits (generated from SYSTEM_PLANS so it
 * can never drift from the real limits), trial mechanics, SMS flow, and how
 * the telemetry scores are computed.
 */

import { ChatCircleDots, Clock, Info, Pulse } from "@phosphor-icons/react";
import { SYSTEM_PLANS } from "../../../../services/nivFirebase";

const PLAN_COLUMNS = ["trial", "starter", "starter-plus", "premium", "custom1"];
const PLAN_BADGE_CLASS = {
  trial: "trial",
  starter: "starter",
  "starter-plus": "starter-plus",
  premium: "premium",
  custom1: "pilot",
};

const unlimited = (n) => (n === null || n === undefined || n >= 1000 ? "ללא הגבלה" : String(n));
const price = (p) => (p ? `₪${p.toFixed(2).replace(/\.?0+$/, "")}` : "חינם");

function NivInfoTool() {
  const plans = PLAN_COLUMNS.map((code) => SYSTEM_PLANS[code]).filter(Boolean);

  const rows = [
    { label: "מחיר לחודש", render: (p) => price(p.monthlyPrice) },
    { label: "SMS לחודש", render: (p) => unlimited(p.limits?.smsPerMonth) },
    { label: "פעולות AI לחודש", render: (p) => unlimited(p.limits?.aiActionsPerMonth) },
    { label: "מטופלים", render: (p) => unlimited(p.limits?.maxPatients) },
    {
      label: "חדרי טיפול",
      render: (p) => (p.features?.canCreateRooms ? unlimited(p.limits?.maxRooms) : "—"),
    },
    {
      label: "מטפלים",
      render: (p) => (p.features?.canAddTherapists ? unlimited(p.limits?.maxTherapists) : "1 (סולו)"),
    },
    { label: "אינטגרציית חשבוניות", render: (p) => (p.features?.canIntegrateMorning ? "✓" : "✗") },
    { label: "סליקה דרך Grow", render: (p) => (p.features?.canIntegrateGrow ? "✓" : "✗") },
    { label: "קליטת מתעניינים (Webhook)", render: (p) => (p.features?.canUseLeadsWebhook === false ? "✗" : "✓") },
  ];

  return (
    <div className="niv-info-content">
      <div className="niv-info-card">
        <div className="niv-info-card-header">
          <Info size={22} weight="fill" />
          <h3>סוגי תוכניות והגבלות</h3>
        </div>
        <div className="niv-info-card-body">
          <div className="niv-info-table-scroll">
            <table className="niv-info-table">
              <thead>
                <tr>
                  <th></th>
                  {plans.map((p) => (
                    <th key={p.code}>
                      <span className={`niv-plan-col-badge ${PLAN_BADGE_CLASS[p.code] || ""}`}>
                        {p.name} ({p.nameHebrew})
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label}>
                    <td className="niv-info-row-label">{row.label}</td>
                    {plans.map((p) => (
                      <td key={p.code}>{row.render(p)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="niv-info-footnote">
            * <strong>פיילוט - חינם</strong> זהה ל-Premium ביכולות ובמגבלות, ללא תשלום חודשי.
            קוד המסלול ב-DB: <code>custom1</code>. מעבר מ-Premium לפיילוט שקוף למטפל.
            <br />* תוכניות Custom (מותאם 2-10) זהות ל-Premium ושמורות לקבוצות מותאמות.
            <br />* הטבלה נבנית מ-<code>SYSTEM_PLANS</code> — תמיד תואמת למגבלות בפועל.
          </p>
        </div>
      </div>

      <div className="niv-info-card">
        <div className="niv-info-card-header">
          <Clock size={22} weight="fill" />
          <h3>מידע על Trial</h3>
        </div>
        <div className="niv-info-card-body">
          <ul className="niv-info-list">
            <li>
              <strong>משך הניסיון:</strong> 30 ימים מרגע ההרשמה (נקבע ביצירת הקליניקה)
            </li>
            <li><strong>ערך ב-DB:</strong> <code>plan: "trial"</code> + <code>trialEndDate</code></li>
            <li>
              <strong>אחרי הניסיון:</strong> כל פעולות הכתיבה חסומות (דרך <code>guardTrialWrite</code>)
            </li>
            <li>
              <strong>מודאל חוסם:</strong> מוצג למשתמש ללא אפשרות סגירה — עם כפתורי יצירת קשר
              (WhatsApp + מייל)
            </li>
            <li><strong>התראות לפני סיום:</strong> מודאל אזהרה 3, 2 ויום אחד לפני הסיום</li>
            <li>
              <strong>מעבר לתוכנית בתשלום:</strong> מהכרטיס של הקליניקה (טאב מסלול ותשלומים)
            </li>
          </ul>
        </div>
      </div>

      <div className="niv-info-card">
        <div className="niv-info-card-header">
          <ChatCircleDots size={22} weight="fill" />
          <h3>תהליך הודעות SMS</h3>
        </div>
        <div className="niv-info-card-body">
          <ul className="niv-info-list">
            <li>
              <strong>הודעת יצירת פגישה:</strong> נשלחת מיד כשנוצרת פגישה חדשה (אם המטפל סימן
              "שלח SMS")
            </li>
            <li>
              <strong>סדרת מפגשים:</strong> הודעה ראשונה עם פרטי כל הסדרה. לפני כל מפגש נשלחת
              תזכורת אוטומטית
            </li>
            <li>
              <strong>תזכורת יום לפני:</strong> Scheduled Job יומי ב-18:00 (שעון ישראל) לפגישות
              של מחר
            </li>
            <li>
              <strong>תזכורת X שעות לפני:</strong> Cloud Task לפי הגדרות המטפל (ברירת מחדל: 2
              שעות)
            </li>
            <li><strong>כל הודעה כוללת:</strong> קישור לאישור/דחיית הגעה</li>
          </ul>
        </div>
      </div>

      <div className="niv-info-card">
        <div className="niv-info-card-header">
          <Pulse size={22} weight="fill" />
          <h3>איך נמדד השימוש בקליניקה</h3>
        </div>
        <div className="niv-info-card-body">
          <ul className="niv-info-list">
            <li>
              <strong>כניסות:</strong> כניסה אחרונה, מספר הכניסות וימים פעילים ב-30 יום,
              וכמה מטפלים באמת נכנסו. "כניסה אחרונה" היא המאוחרת מבין
              <code>lastLoginDate</code> שבכרטיס המטפל לבין הפעילות האחרונה בטלמטריה, כך
              שגם קליניקות ותיקות מדווחות נכון.
            </li>
            <li>
              <strong>חיבורים ואינטגרציות:</strong> יומן Google, Morning, חשבונית דיגיטלית,
              חדרים, התראות פוש, פגישות וידאו, שאלונים וחתימה דיגיטלית — מצב חיבור בפועל
              מתוך Firestore, לא מתוך לחיצות.
            </li>
            <li>
              <strong>פגישות אחורה:</strong> 7 ו-30 הימים האחרונים, בפילוח הושלמו / בוטלו /
              לא עודכנו. "לא עודכנו" הן פגישות שהזמן שלהן עבר ואיש לא נגע בהן.
            </li>
            <li>
              <strong>פגישות קדימה:</strong> 7 ו-30 הימים הקרובים. יומן ריק קדימה הוא
              הסימן הברור ביותר לנטישה.
            </li>
            <li>
              <strong>מקור הנתונים לפיצ'רים:</strong> המקסימום בין טלמטריית הלקוח (לחיצות
              באפליקציה) ל"אמת מהשרת" — מצב אינטגרציות וספירת מסמכים ב-Firestore. לכן
              אינטגרציה מחוברת נספרת כשימוש גם בלי לחיצה.
            </li>
            <li>
              <strong>מצבי פיצ'ר:</strong> פעיל (30 יום) · לאחרונה (60 יום) · מחובר ללא שימוש ·
              לא בשימוש X ימים · מעולם לא.
            </li>
            <li>
              <strong>עדכון:</strong> חישוב לילי ב-02:45 אל <code>niv_clinic_usage</code>.
              כפתור "חשב מחדש" בטאב שימוש ומדדים ובכרטיס קליניקה מחשב מיד, כולל שימוש מהיום.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default NivInfoTool;
